import * as THREE from 'three';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';
import {ProductApi} from 'components/customer/Product/store/productApi';
import {RootState} from 'store/customer';
import {DEFAULT_POSITION_Y} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';

interface CabinetInitialPositionParams {
    product: RoomPlannerProductWithTemplate;
    cabinetGroup: THREE.Group;
    variables: TemplateVariable;
    templateName: string;
    state: RootState;
    room: string;
    wallHeight: number;
    roomDepthComputed: number;
    defaultPositionZ: number | undefined;
    sitOnFloor: boolean;
    isBulkheadComponent: boolean;
    isUpperProduct: boolean;
    height: number;
}

interface CabinetInitialPosition {
    positionY: number;
    positionZ: number;
    computedDepth: number;
    upperCabinetHeight: number;
    toeKickHeight: number;
    extendsToFloor: boolean;
}

/**
 * Converts a value to a number, falling back to the default when it is
 * missing or not numeric. Unlike `||`, a legitimate 0 is kept.
 * @param {unknown} value - The value to convert
 * @param {number} defaultValue - Fallback when the value isn't a number
 * @return {number} The parsed number or the default
 */
export const toNumberOrDefault = (
    value: unknown,
    defaultValue: number
): number => {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Calculates the initial Y and Z positions for a cabinet based on its type and properties
 * @return {CabinetInitialPosition} Object containing position coordinates and calculated values
 */
export const calculateCabinetInitialPosition = ({
    product,
    cabinetGroup,
    state,
    room,
    wallHeight,
    roomDepthComputed,
    defaultPositionZ,
    sitOnFloor,
    isBulkheadComponent,
    isUpperProduct,
    height,
}: CabinetInitialPositionParams): CabinetInitialPosition => {
    const computedDepth = Number(cabinetGroup.userData.depth) || height;

    // Calculate toe kick height
    const toeKickScale = Number(product.toe_kick_height) * (81 / 150);
    const toeKickHeight = product.toe_kick_height
        ? toeKickScale + (81 - toeKickScale) * 0.63
        : 81;

    // Get room details for height calculations
    const roomDetails = ProductApi.endpoints.getRoomProducts.select({
        roomId: parseInt(room),
    })(state as never)?.data?.roomDetails;

    // Without a toe kick the cabinet sits directly on the floor.
    const hasZeroToeKickHeight = product.toe_kick_height == 0;

    // Calculate upper cabinet position
    const upperCabinetHeight =
        Number(product.cabinet_height) || roomDetails?.upperHeight || 550;
    const toeKickHeightDefault = toNumberOrDefault(
        roomDetails?.toeKickHeight,
        150
    );
    const tallHeight = roomDetails?.tallHeight || 1950;
    const upperCabinetScale =
        (toeKickHeightDefault +
            tallHeight -
            (isBulkheadComponent ? 0 : upperCabinetHeight)) *
        (451 / 2000);
    const upperCabinetPositionY =
        upperCabinetScale + (451 - upperCabinetScale) * 0.113;

    // Check if cabinet extends to floor
    const extendsToFloor = Boolean(
        (product as {cabinet_extend?: number})?.cabinet_extend
    );

    // Calculate Y position based on cabinet type
    const positionY =
        -(wallHeight / 2) +
        (isUpperProduct
            ? upperCabinetPositionY
            : sitOnFloor || hasZeroToeKickHeight
              ? DEFAULT_POSITION_Y.sitOnFloor
              : toeKickHeight);

    // Calculate Z position
    const positionZ =
        defaultPositionZ ?? roomDepthComputed + computedDepth + 12.46;

    return {
        positionY,
        positionZ,
        computedDepth,
        upperCabinetHeight,
        toeKickHeight,
        extendsToFloor,
    };
};
